"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { ThumbsDown, ThumbsUp, X } from "lucide-react";
import { useState } from "react";
import { localePath } from "@/lib/i18n/locales";
import { useLocale, useT } from "@/lib/use-i18n";
import { cn } from "@/lib/utils";
export type UsefulnessNegativePayload = {
  message?: string | null;
  reasonCode?: string | null;
};
export type UsefulnessPromptProps = {
  onDismiss: () => void;
  onPositive: () => void;
  /** Called when the user finishes the “no” path (with optional reason). */
  onNegative: (payload?: UsefulnessNegativePayload) => void;
  className?: string;
  /** Salida animada (la provee CareDockHost vía useAnimatedOpen). */
  leaving?: boolean;
};
const REASON_CHIPS = [{
  id: "quality",
  key: "usefulnessReasonQuality"
}, {
  id: "unchanged",
  key: "usefulnessReasonUnchanged"
}, {
  id: "wrong_lang",
  key: "usefulnessReasonWrongLang"
}, {
  id: "too_formal",
  key: "usefulnessReasonTooFormal"
}, {
  id: "other",
  key: "usefulnessReasonOther"
}] as const;

/**
 * Occasional lightweight “was this useful?” control after tool success.
 * Negative path asks for a short reason, then offers contact / suggestions.
 */
export function UsefulnessPrompt(t0) {
  const $ = _c(57);
  const {
    onDismiss,
    onPositive,
    onNegative,
    className,
    leaving: t1
  } = t0;
  const leaving = t1 === undefined ? false : t1;
  const t = useT();
  const locale = useLocale();
  let t2;
  if ($[0] !== locale) {
    t2 = path => localePath(locale, path);
    $[0] = locale;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const lp = t2;
  const [phase, setPhase] = useState("ask");
  const [reasonCode, setReasonCode] = useState(null);
  const [reasonText, setReasonText] = useState("");
  let t3;
  if ($[2] !== onNegative) {
    t3 = payload => {
      onNegative(payload);
      setPhase("reason_sent");
    };
    $[2] = onNegative;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const finishNegative = t3;
  let t4;
  if ($[4] !== finishNegative || $[5] !== reasonCode || $[6] !== reasonText) {
    t4 = () => {
      const message = reasonText.trim().slice(0, 500) || null;
      finishNegative({
        reasonCode: reasonCode || null,
        message
      });
    };
    $[4] = finishNegative;
    $[5] = reasonCode;
    $[6] = reasonText;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const submitReason = t4;
  let t5;
  if ($[8] !== className) {
    t5 = cn("pointer-events-none fixed inset-x-0 bottom-0 z-[80] flex justify-center p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:p-4", className);
    $[8] = className;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = leaving ? "animate-out fade-out slide-out-to-bottom-1 duration-150 motion-reduce:animate-none" : "animate-in fade-in slide-in-from-bottom-1 duration-150 motion-reduce:animate-none";
  let t7;
  if ($[10] !== t6) {
    t7 = cn("care-dock-panel pointer-events-auto relative w-full max-w-[26rem] overflow-hidden", "rounded-[var(--radius-card)] border border-[var(--border-strong)]", "bg-[rgba(14,14,20,0.72)] shadow-[var(--shadow-raised)]", "backdrop-blur-sm backdrop-saturate-150", "supports-[backdrop-filter]:bg-[rgba(14,14,20,0.55)]", t6);
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/35 to-transparent" aria-hidden="true" />;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== onPositive || $[14] !== phase || $[15] !== t) {
    t9 = phase === "ask" && <><p id="usefulness-title" className="text-[var(--step--1)] font-semibold leading-snug text-foreground">{t("usefulnessTitle")}</p><div className="mt-3 flex flex-wrap gap-2"><button type="button" data-testid="usefulness-yes" onClick={() => {
          setPhase("thanks");
          onPositive();
        }} className={cn("inline-flex min-h-9 items-center gap-1.5 rounded-full border border-[var(--border-strong)]", "bg-transparent px-3.5 text-[var(--step--2)] font-medium text-foreground", "hover:bg-[var(--muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/35", "active:scale-[0.98]")}><ThumbsUp className="size-3.5" aria-hidden="true" />{t("usefulnessYes")}</button><button type="button" data-testid="usefulness-no" onClick={() => {
          setPhase("negative");
        }} className={cn("inline-flex min-h-9 items-center gap-1.5 rounded-full border border-[var(--border-strong)]", "bg-transparent px-3.5 text-[var(--step--2)] font-medium text-foreground-soft", "hover:bg-[var(--muted)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/35", "active:scale-[0.98]")}><ThumbsDown className="size-3.5" aria-hidden="true" />{t("usefulnessNo")}</button></div></>;
    $[13] = onPositive;
    $[14] = phase;
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== phase || $[18] !== t) {
    t10 = phase === "thanks" && <p id="usefulness-title" className="text-[var(--step--1)] font-medium text-foreground">{t("usefulnessThanks")}</p>;
    $[17] = phase;
    $[18] = t;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== finishNegative || $[21] !== lp || $[22] !== phase || $[23] !== reasonCode || $[24] !== reasonText || $[25] !== submitReason || $[26] !== t) {
    t11 = phase === "negative" && <><p id="usefulness-title" className="text-[var(--step--1)] font-semibold leading-snug text-foreground">{t("usefulnessNegativeTitle")}</p><p className="mt-0.5 text-[var(--step--2)] text-muted-foreground">{t("usefulnessNegativeBody")}</p><p className="mt-3 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">{t("usefulnessReasonLabel")}</p><div className="mt-1.5 flex flex-wrap gap-1.5" role="group" aria-label={t("usefulnessReasonLabel")}>{REASON_CHIPS.map(chip => {
          const selected = reasonCode === chip.id;
          return <button key={chip.id} type="button" data-testid={`usefulness-reason-${chip.id}`} aria-pressed={selected} onClick={() => setReasonCode(selected ? null : chip.id)} className={cn("inline-flex min-h-8 items-center rounded-full border px-2.5 text-[11.5px] font-medium transition-colors", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/35", selected ? "border-[var(--accent)] bg-[color-mix(in_srgb,var(--accent)_14%,transparent)] text-[var(--accent)]" : "border-[var(--border-strong)] text-foreground-soft hover:bg-[var(--muted)] hover:text-foreground")}>{t(chip.key)}</button>;
        })}</div><label className="mt-3 block"><span className="sr-only">{t("usefulnessReasonPlaceholder")}</span><textarea data-testid="usefulness-reason-text" value={reasonText} onChange={e => setReasonText(e.target.value.slice(0, 500))} rows={2} maxLength={500} placeholder={t("usefulnessReasonPlaceholder")} className={cn("w-full resize-none rounded-[var(--radius-control)] border border-[var(--border-strong)]", "bg-[var(--muted)]/40 px-3 py-2 text-[var(--step--2)] text-foreground placeholder:text-muted-foreground", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/35")} /></label><div className="mt-2.5 flex flex-wrap gap-2"><button type="button" data-testid="usefulness-reason-submit" onClick={submitReason} className={cn("inline-flex min-h-9 items-center rounded-full bg-[var(--accent)]", "px-3.5 text-[var(--step--2)] font-semibold text-[var(--accent-foreground)] hover:opacity-90", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/35", "active:scale-[0.98]")}>{t("usefulnessReasonSubmit")}</button><button type="button" data-testid="usefulness-reason-skip" onClick={() => finishNegative()} className={cn("inline-flex min-h-9 items-center rounded-full border border-[var(--border-strong)]", "px-3.5 text-[var(--step--2)] font-medium text-foreground-soft hover:bg-[var(--muted)] hover:text-foreground", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/35")}>{t("usefulnessReasonSkip")}</button></div><div className="mt-3 flex flex-wrap gap-2 border-t border-[var(--border)]/70 pt-3"><Link href={lp("/contact")} data-testid="usefulness-contact" onClick={() => finishNegative({
          reasonCode,
          message: reasonText.trim() || null
        })} className={cn("inline-flex min-h-9 items-center rounded-full border border-[var(--border-strong)]", "px-3.5 text-[var(--step--2)] font-medium text-foreground-soft hover:bg-[var(--muted)] hover:text-foreground")}>{t("careDockReportError")}</Link><Link href={lp("/suggestions")} data-testid="usefulness-suggestions" onClick={() => finishNegative({
          reasonCode,
          message: reasonText.trim() || null
        })} className={cn("inline-flex min-h-9 items-center rounded-full border border-[var(--border-strong)]", "px-3.5 text-[var(--step--2)] font-medium text-foreground-soft hover:bg-[var(--muted)] hover:text-foreground")}>{t("careDockSuggest")}</Link></div></>;
    $[20] = finishNegative;
    $[21] = lp;
    $[22] = phase;
    $[23] = reasonCode;
    $[24] = reasonText;
    $[25] = submitReason;
    $[26] = t;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== lp || $[29] !== phase || $[30] !== t) {
    t12 = phase === "reason_sent" && <><p id="usefulness-title" className="text-[var(--step--1)] font-medium text-foreground">{t("usefulnessReasonThanks")}</p><div className="mt-3 flex flex-wrap gap-2"><Link href={lp("/contact")} data-testid="usefulness-contact" className={cn("inline-flex min-h-9 items-center rounded-full border border-[var(--border-strong)]", "px-3.5 text-[var(--step--2)] font-medium text-foreground-soft hover:bg-[var(--muted)] hover:text-foreground")}>{t("careDockReportError")}</Link><Link href={lp("/suggestions")} data-testid="usefulness-suggestions" className={cn("inline-flex min-h-9 items-center rounded-full bg-[var(--accent)]", "px-3.5 text-[var(--step--2)] font-semibold text-[var(--accent-foreground)] hover:opacity-90")}>{t("careDockSuggest")}</Link></div></>;
    $[28] = lp;
    $[29] = phase;
    $[30] = t;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t12 || $[35] !== t9) {
    t13 = <div className="min-w-0 flex-1">{t9}{t10}{t11}{t12}</div>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
    $[35] = t9;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== finishNegative || $[38] !== onDismiss || $[39] !== phase) {
    t14 = () => {
      if (phase === "negative") {
        finishNegative();
      }
      onDismiss();
    };
    $[37] = finishNegative;
    $[38] = onDismiss;
    $[39] = phase;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = cn("inline-flex size-9 shrink-0 items-center justify-center rounded-full", "text-muted-foreground hover:bg-[var(--muted)] hover:text-foreground", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/35");
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t) {
    t16 = t("usefulnessClose");
    $[42] = t;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <X className="size-3.5" aria-hidden="true" />;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== t14 || $[46] !== t16) {
    t18 = <button type="button" data-testid="usefulness-close" onClick={t14} className={t15} aria-label={t16}>{t17}</button>;
    $[45] = t14;
    $[46] = t16;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== t13 || $[49] !== t18) {
    t19 = <div className="relative flex items-start gap-2 p-3.5 sm:p-4">{t13}{t18}</div>;
    $[48] = t13;
    $[49] = t18;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== t19 || $[52] !== t7) {
    t20 = <div role="region" aria-labelledby="usefulness-title" data-testid="usefulness-prompt" className={t7}>{t8}{t19}</div>;
    $[51] = t19;
    $[52] = t7;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== t20 || $[55] !== t5) {
    t21 = <div className={t5} data-testid="usefulness-root">{t20}</div>;
    $[54] = t20;
    $[55] = t5;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  return t21;
}